import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteField,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { useCollection } from 'react-firebase-hooks/firestore';
import { db, auth } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath } from '@elka/firebase';
import { isPaidOn } from '@elka/utils';
import type { PettyCashReceipt } from '@elka/types';
import { useIsAdmin } from '@/features/auth/hooks/useIsAdmin';

const pettyCashCollection = () => collection(db, ...splitPath(COLLECTIONS.team.pettyCash));
const receiptRef = (id: string) => doc(db, ...splitPath(COLLECTIONS.team.pettyCash), id);

function reviewer() {
  if (!auth.currentUser) throw new Error('Not authenticated');
  return auth.currentUser.displayName || auth.currentUser.email || 'Unknown';
}

export function useAdminPettyCash() {
  // ponytail: whole history, no window — add a createdAt cutoff if the collection grows past a few thousand.
  const receiptsQuery = useMemo(
    () => query(pettyCashCollection(), orderBy('createdAt', 'desc')),
    []
  );
  const [snapshot, loading, error] = useCollection(receiptsQuery);

  const receipts = useMemo(
    () =>
      snapshot?.docs.map(
        (d) => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) }) as PettyCashReceipt
      ) ?? [],
    [snapshot]
  );

  const approve = (id: string, approvedAmount: number, managerNote: string) =>
    updateDoc(receiptRef(id), {
      status: 'approved',
      approvedAmount,
      managerNote: managerNote || deleteField(),
      reviewedBy: reviewer(),
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

  const deny = (id: string, managerNote: string) =>
    updateDoc(receiptRef(id), {
      status: 'denied',
      managerNote,
      reviewedBy: reviewer(),
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

  /** Thursday payout: every approved receipt of the worker becomes paid. */
  const markPaid = async (workerId: string) => {
    const batch = writeBatch(db);
    for (const r of receipts) {
      if (r.workerId === workerId && r.status === 'approved') {
        batch.update(receiptRef(r.id), {
          status: 'paid',
          paidAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }
    await batch.commit();
  };

  /** Undo today's payout for the worker — back to approved. */
  const undoPaid = async (workerId: string) => {
    const today = new Date();
    const batch = writeBatch(db);
    for (const r of receipts) {
      if (r.workerId === workerId && isPaidOn(r, today)) {
        batch.update(receiptRef(r.id), {
          status: 'approved',
          paidAt: deleteField(),
          updatedAt: serverTimestamp(),
        });
      }
    }
    await batch.commit();
  };

  return {
    receipts,
    isLoading: loading,
    error: error?.message ?? null,
    approve,
    deny,
    markPaid,
    undoPaid,
  };
}

/** Pending receipts for the sidebar badge; admins only (the rules deny everyone else). */
export function usePendingPettyCashCount(): number {
  const isAdmin = useIsAdmin();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    return onSnapshot(
      query(pettyCashCollection(), where('status', '==', 'pending')),
      (snap) => setCount(snap.size),
      (err) => console.error('Failed to load pending petty cash count:', err)
    );
  }, [isAdmin]);

  return isAdmin ? count : 0;
}
